import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  ArrowLeft,
  Building2,
  User,
  Phone,
  Mail,
  MapPin,
  Globe,
  Save,
  Loader2,
  Upload,
  Users,
  Image,
  X
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import AnimatedLogo from "../components/AnimatedLogo";
import ThemeToggle from "../components/ThemeToggle";
import UserMenu from "../components/UserMenu";
import api from "../lib/api";
import { getImageUrl } from "../lib/utils";
import { useRef } from "react";

export default function CompanySettingsPage() {
  const { user, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    company_name: "",
    contact_name: "",
    client_type: "entreprise",
    phone: "",
    address: "",
    city: "",
    postal_code: "",
    country: "France",
    website: "",
    description: "",
    logo_url: ""
  });

  useEffect(() => {
    if (user) {
      setFormData({
        company_name: user.company_name || "",
        contact_name: user.contact_name || "",
        client_type: user.client_type || "entreprise",
        phone: user.phone || "",
        address: user.address || "",
        city: user.city || "",
        postal_code: user.postal_code || "",
        country: user.country || "France",
        website: user.website || "",
        description: user.description || "",
        logo_url: user.logo_url || ""
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await api.put("/users/me", formData);
      if (updateUser) {
        updateUser(response.data);
      }
      toast.success("Informations mises à jour");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Veuillez sélectionner une image");
      return;
    }
    
    // Validate size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5 Mo");
      return;
    }
    
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      
      const response = await api.post("/upload", formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      const logoUrl = response.data.url;
      
      // Update form data
      setFormData(prev => ({ ...prev, logo_url: logoUrl }));
      
      // Auto-save to backend
      const saveResponse = await api.put("/users/me", { ...formData, logo_url: logoUrl });
      if (updateUser) {
        updateUser(saveResponse.data);
      }
      
      toast.success("Logo enregistré");
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = async () => {
    try {
      setFormData(prev => ({ ...prev, logo_url: "" }));
      
      // Auto-save removal to backend
      const saveResponse = await api.put("/users/me", { ...formData, logo_url: "" });
      if (updateUser) {
        updateUser(saveResponse.data);
      }
      
      toast.success("Logo supprimé");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <Link to="/settings">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <AnimatedLogo linkTo="/dashboard" showAnimation={true} size="small" />
          </div>
          <div className="flex items-center gap-1 sm:gap-3">
            <ThemeToggle />
            <UserMenu />
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-3" style={{ fontFamily: 'Outfit' }}>
            <Building2 className="w-8 h-8 text-primary" />
            Mon {formData.client_type === "association" ? "association" : "entreprise"}
          </h1>
          <p className="text-muted-foreground mt-1">
            Gérez les informations de votre organisation
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Logo */}
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Image className="w-5 h-5 text-primary" />
                Logo
              </CardTitle>
              <CardDescription>
                Le logo apparaîtra sur vos tirages et sondages
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-6">
                <div className="relative">
                  {formData.logo_url ? (
                    <div className="relative w-24 h-24 rounded-xl border-2 border-dashed border-border overflow-hidden bg-muted">
                      <img 
                        src={getImageUrl(formData.logo_url)}
                        alt="Logo" 
                        className="w-full h-full object-contain p-2"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <button
                        type="button"
                        onClick={removeLogo}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center hover:bg-destructive/90"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      className="w-24 h-24 rounded-xl border-2 border-dashed border-border flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
                    >
                      {uploading ? (
                        <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-6 h-6 text-muted-foreground mb-1" />
                          <span className="text-xs text-muted-foreground">Ajouter</span>
                        </>
                      )}
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                </div>
                <div className="text-sm text-muted-foreground">
                  <p>Format recommandé : PNG ou SVG</p>
                  <p>Taille max : 5 Mo</p>
                  <p>Résolution idéale : 200x200 px</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Type de client */}
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Type d'organisation
              </CardTitle>
              <CardDescription>
                Indiquez si vous êtes une entreprise ou une association
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Select
                value={formData.client_type}
                onValueChange={(value) => handleChange("client_type", value)}
              >
                <SelectTrigger className="w-full" data-testid="client-type-select">
                  <SelectValue placeholder="Type d'organisation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="entreprise">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      <span>Entreprise</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="association">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      <span>Association</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Informations principales */}
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="text-lg">Informations principales</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">
                    {formData.client_type === "association" ? "Nom de l'association" : "Nom de l'entreprise"} *
                  </Label>
                  <div className="relative">
                    <Building2 className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
                    <Input
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => handleChange("company_name", e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_name">Nom du contact *</Label>
                  <div className="relative">
                    <User className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
                    <Input
                      id="contact_name"
                      value={formData.contact_name}
                      onChange={(e) => handleChange("contact_name", e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone</Label>
                  <div className="relative">
                    <Phone className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      className="pl-10"
                      placeholder="01 23 45 67 89"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website">Site web</Label>
                  <div className="relative">
                    <Globe className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
                    <Input
                      id="website"
                      type="url"
                      value={formData.website}
                      onChange={(e) => handleChange("website", e.target.value)}
                      className="pl-10"
                      placeholder="https://mon-site.com"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  placeholder="Décrivez votre activité..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Adresse */}
          <Card className="bg-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5 text-primary" />
                Adresse
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="address">Adresse</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => handleChange("address", e.target.value)}
                  placeholder="123 rue de l'exemple"
                />
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="postal_code">Code postal</Label>
                  <Input
                    id="postal_code"
                    value={formData.postal_code}
                    onChange={(e) => handleChange("postal_code", e.target.value)}
                    placeholder="75001"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">Ville</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => handleChange("city", e.target.value)}
                    placeholder="Paris"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Pays</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => handleChange("country", e.target.value)}
                    placeholder="France"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end">
            <Button type="submit" disabled={saving} className="rounded-full">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer
                </>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
